import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  InputAdornment,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import ReplayIcon from '@material-ui/icons/Replay';
import SaveIcon from '@material-ui/icons/Save';
import SearchIcon from '@material-ui/icons/Search';
import SearchFeaturePageShell from '../../components/shell/SearchFeaturePageShell';
import SettingsCard from '../../components/kea/SettingsCard/SettingsCard';
import { ADMIN_RUNTIME_COMMANDS } from '../../app/runtime/adminRuntime';
import { notifyOperatorStateRefresh } from '../home/livePublishEvents';
import { useShellAppContext } from '../shell/context/useShellAppContext';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';
import { getTranslationPresentation } from './translationPresentation';

const useStyles = makeStyles((theme) => ({
  root: {
    color: '#1e2248',
    fontFamily: "'Roboto', sans-serif",
    '& .MuiTypography-h6': {
      color: '#1e2248',
      fontSize: 18,
      fontWeight: 600,
      letterSpacing: '-0.015em',
      lineHeight: 1.35,
    },
    '& .MuiTypography-body2': {
      color: '#66708a',
      fontSize: 13,
      lineHeight: 1.55,
    },
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  intro: {
    maxWidth: 680,
    marginBottom: theme.spacing(2.5),
  },
  pageActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  unsaved: {
    color: '#8a6d2f',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'capitalize',
  },
  languageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
    gap: theme.spacing(1.25),
  },
  languageOption: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    padding: theme.spacing(1.25, 1.5),
    border: '1px solid #dde3ea',
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '&:hover': {
      borderColor: '#b5d8cb',
      backgroundColor: '#fbfefd',
    },
    '&:focus-within': {
      boxShadow: '0 0 0 3px rgba(var(--kea-color-primary-rgb), 0.12)',
    },
  },
  languageOptionSelected: {
    borderColor: '#8fcbb4',
    backgroundColor: '#f3fbf8',
  },
  languageCheckbox: {
    padding: theme.spacing(0.5, 1.25, 0.5, 0),
  },
  languageName: {
    display: 'block',
    color: '#1e2248',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.35,
  },
  languageMeta: {
    display: 'block',
    marginTop: 2,
    color: '#7b849a',
    fontSize: 12,
    lineHeight: 1.35,
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(0.5, 0, 1.5),
    minHeight: 40,
    '& .MuiTab-root': {
      minWidth: 64,
      minHeight: 40,
      padding: theme.spacing(0.75, 2),
      fontWeight: 600,
    },
  },
  translationToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(0.75, 0, 1.5),
    [theme.breakpoints.down('xs')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
    },
  },
  search: {
    width: 380,
    maxWidth: '100%',
  },
  searchIcon: {
    color: theme.palette.text.secondary,
    fontSize: 20,
  },
  translationList: {
    overflow: 'hidden',
    border: '1px solid #e8eaf0',
    borderRadius: 8,
  },
  translationRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(210px, 0.75fr) minmax(300px, 1.75fr) 72px',
    alignItems: 'start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 0,
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: theme.spacing(1),
    },
  },
  translationMeta: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minHeight: 40,
  },
  translationTitleRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  translationTitle: {
    color: '#1e2248',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.35,
  },
  translationDescription: {
    maxWidth: 280,
    color: '#7b849a',
    fontSize: 12,
    lineHeight: 1.45,
  },
  overrideChip: {
    height: 20,
    borderRadius: 4,
    color: 'var(--kea-color-primary)',
    backgroundColor: 'var(--kea-color-primary-soft)',
    fontSize: 11,
    fontWeight: 600,
  },
  translationInput: {
    '& .MuiOutlinedInput-input': {
      paddingTop: 10,
      paddingBottom: 10,
    },
    '& .MuiFormHelperText-root': {
      marginTop: theme.spacing(0.5),
      lineHeight: 1.35,
    },
  },
  rowActions: {
    minHeight: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    textAlign: 'right',
    [theme.breakpoints.down('sm')]: {
      minHeight: 0,
      justifyContent: 'flex-start',
      textAlign: 'left',
    },
  },
  emptyState: {
    padding: theme.spacing(3, 0),
    textAlign: 'center',
  },
  templateMeta: {
    color: '#66708a',
    fontFamily: '"Roboto Mono", "Courier New", monospace',
    fontSize: 12,
  },
  templateSource: {
    width: '100%',
    minHeight: 320,
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    boxSizing: 'border-box',
    fontFamily: '"Roboto Mono", "Courier New", monospace',
    fontSize: 13,
    lineHeight: 1.55,
    resize: 'vertical',
  },
}));

const LANGUAGE_FALLBACK_NAMES = {
  de: 'German',
  en: 'English',
  es: 'Spanish (Spain)',
  'es-MX': 'Spanish (Mexico)',
  fr: 'French',
  it: 'Italian',
  nl: 'Dutch',
  pl: 'Polish',
  pt: 'Portuguese',
  uk: 'Ukrainian',
};

const LANGUAGE_REGION_NAMES = {
  es: LANGUAGE_FALLBACK_NAMES.es,
  'es-MX': LANGUAGE_FALLBACK_NAMES['es-MX'],
};

function languageName(language, locale) {
  if (LANGUAGE_REGION_NAMES[language]) return LANGUAGE_REGION_NAMES[language];
  try {
    return new Intl.DisplayNames([locale], { type: 'language' }).of(language);
  } catch (_error) {
    return LANGUAGE_FALLBACK_NAMES[language] || language.toUpperCase();
  }
}

function cloneOverrides(overrides) {
  return Object.fromEntries(
    Object.entries(overrides || {}).map(([language, values]) => [language, { ...values }]),
  );
}

export default function StorefrontContentPage() {
  const classes = useStyles();
  const intl = useIntl();
  const t = useLocalizedMessage();
  const { gatewayClient, routeShopId, runtime } = useShellAppContext();
  const [content, setContent] = useState(null);
  const [enabledLanguages, setEnabledLanguages] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [savedOverrides, setSavedOverrides] = useState({});
  const [templateOverrides, setTemplateOverrides] = useState({});
  const [savedTemplateOverrides, setSavedTemplateOverrides] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [languageWarningOpen, setLanguageWarningOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await gatewayClient.loadStorefrontContent();
      const languages = next.localization.enabledLanguages;
      const nextOverrides = cloneOverrides(next.localization.overrides);
      setContent(next);
      setEnabledLanguages(languages);
      setSavedLanguages(languages);
      setOverrides(nextOverrides);
      setSavedOverrides(cloneOverrides(nextOverrides));
      const nextTemplateOverrides = { ...(next.templates?.overrides || {}) };
      const templateIds = Object.keys(next.templates?.items || {});
      setTemplateOverrides(nextTemplateOverrides);
      setSavedTemplateOverrides({ ...nextTemplateOverrides });
      setSelectedTemplate(current => (
        templateIds.includes(current) ? current : (templateIds[0] || '')
      ));
      setSelectedLanguage(current => (
        languages.includes(current) ? current : languages[0]
      ));
    } catch (loadError) {
      setError(loadError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (gatewayClient) load();
    else {
      setError(new Error(t('search.page.storefrontContent.unavailable')));
      setLoading(false);
    }
    // The shell owns gatewayClient lifetime; reload only when that transport changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayClient]);

  const dirty = useMemo(() => (
    JSON.stringify(enabledLanguages) !== JSON.stringify(savedLanguages)
      || JSON.stringify(overrides) !== JSON.stringify(savedOverrides)
      || JSON.stringify(templateOverrides) !== JSON.stringify(savedTemplateOverrides)
  ), [
    enabledLanguages,
    overrides,
    savedLanguages,
    savedOverrides,
    savedTemplateOverrides,
    templateOverrides,
  ]);

  const catalog = content?.localization.catalogs?.[selectedLanguage] || {};
  const languageOverrides = overrides[selectedLanguage] || {};
  const templates = content?.templates?.items || {};
  const selectedTemplateDefinition = templates[selectedTemplate] || null;
  const selectedTemplateSource = templateOverrides[selectedTemplate]
    ?? selectedTemplateDefinition?.source
    ?? '';
  const translationKeys = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return Object.keys(catalog).filter((key) => {
      if (!normalizedQuery) return true;
      const value = languageOverrides[key] ?? catalog[key];
      const presentation = getTranslationPresentation(key);
      return presentation.label.toLowerCase().includes(normalizedQuery)
        || presentation.description.toLowerCase().includes(normalizedQuery)
        || String(value).toLowerCase().includes(normalizedQuery);
    });
  }, [catalog, languageOverrides, query]);

  const addLanguage = (language) => {
    if (!language || enabledLanguages.includes(language)) return;
    setEnabledLanguages(current => [...current, language]);
    setSelectedLanguage(language);
    setLanguageWarningOpen(false);
    setSaved(false);
  };

  const toggleLanguage = (language) => {
    if (enabledLanguages.includes(language)) removeLanguage(language);
    else addLanguage(language);
  };

  const removeLanguage = (language) => {
    if (enabledLanguages.length <= 1) {
      setLanguageWarningOpen(true);
      return;
    }
    const next = enabledLanguages.filter(item => item !== language);
    setEnabledLanguages(next);
    if (selectedLanguage === language) setSelectedLanguage(next[0]);
    setSaved(false);
  };

  const updateTranslation = (key, value) => {
    setOverrides((current) => {
      const nextLanguage = { ...(current[selectedLanguage] || {}) };
      if (value === catalog[key]) delete nextLanguage[key];
      else nextLanguage[key] = value;
      return { ...current, [selectedLanguage]: nextLanguage };
    });
    setSaved(false);
  };

  const resetLanguage = () => {
    setOverrides(current => ({ ...current, [selectedLanguage]: {} }));
    setSaved(false);
  };

  const updateTemplate = (source) => {
    setTemplateOverrides((current) => {
      const next = { ...current };
      if (source === selectedTemplateDefinition.source) delete next[selectedTemplate];
      else next[selectedTemplate] = source;
      return next;
    });
    setSaved(false);
  };

  const save = async () => {
    if (enabledLanguages.length === 0) {
      setLanguageWarningOpen(true);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const languageResult = await gatewayClient.saveStorefrontLanguages(enabledLanguages);
      const persistedLanguages = languageResult?.enabledLanguages || enabledLanguages;
      const changedLanguages = Object.keys(overrides).filter(
        language => JSON.stringify(overrides[language] || {})
          !== JSON.stringify(savedOverrides[language] || {}),
      );
      await Promise.all(changedLanguages.map(language => (
        gatewayClient.saveStorefrontTranslationOverrides(language, overrides[language] || {})
      )));
      const changedTemplates = Object.keys(templates).filter(
        templateId => (templateOverrides[templateId] ?? templates[templateId].source)
          !== (savedTemplateOverrides[templateId] ?? templates[templateId].source),
      );
      await Promise.all(changedTemplates.map(templateId => (
        gatewayClient.saveStorefrontTemplateOverride(
          templateId,
          templateOverrides[templateId] ?? templates[templateId].source,
        )
      )));
      if (runtime?.supports?.(ADMIN_RUNTIME_COMMANDS.SETTINGS_PREVIEW_SAVED)) {
        await runtime.sendMessageWithResponse(
          ADMIN_RUNTIME_COMMANDS.SETTINGS_PREVIEW_SAVED,
          { shopId: routeShopId },
        );
        notifyOperatorStateRefresh();
      }
      setEnabledLanguages([...persistedLanguages]);
      setSavedLanguages([...persistedLanguages]);
      setSavedOverrides(cloneOverrides(overrides));
      setSavedTemplateOverrides({ ...templateOverrides });
      setSaved(true);
    } catch (saveError) {
      setSaveError(saveError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SearchFeaturePageShell
      pageId="storefrontContent"
      description={t('search.page.storefrontContent.description')}
      actions={(
        <Box className={classes.pageActions}>
          {dirty && (
            <Typography className={classes.unsaved}>
              {t('searchInterface.editor.unsavedChangesPlural')}
            </Typography>
          )}
          <Button
            color="primary"
            variant="contained"
            disabled={!dirty || saving}
            onClick={save}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </Box>
      )}
      isLoading={loading}
      error={error}
      onRetry={load}
    >
      <Box className={classes.root}>
        {saveError && (
          <Box className={classes.section}>
            <Alert severity="error">{saveError.message}</Alert>
          </Box>
        )}
        {saved && (
          <Box className={classes.section}>
            <Alert severity="success">{t('search.page.storefrontContent.saved')}</Alert>
          </Box>
        )}

        <SettingsCard className={classes.section}>
          <Typography variant="h6">
            {t('search.page.storefrontContent.languages.title')}
          </Typography>
          <Typography variant="body2" className={classes.intro}>
            {t('search.page.storefrontContent.languages.help')}
          </Typography>
          <Box className={classes.languageGrid}>
            {(content?.localization.availableLanguages || []).map((language) => {
              const isEnabled = enabledLanguages.includes(language);
              return (
                <label
                  key={language}
                  className={[
                    classes.languageOption,
                    isEnabled ? classes.languageOptionSelected : '',
                  ].filter(Boolean).join(' ')}
                >
                  <Checkbox
                    className={classes.languageCheckbox}
                    color="primary"
                    checked={isEnabled}
                    onChange={() => toggleLanguage(language)}
                    inputProps={{ 'aria-label': language.toUpperCase() }}
                  />
                  <span>
                    <span className={classes.languageName}>
                      {languageName(language, intl.locale)}
                    </span>
                    <span className={classes.languageMeta}>
                      {language.toUpperCase()}
                    </span>
                  </span>
                </label>
              );
            })}
          </Box>
        </SettingsCard>

        <SettingsCard className={classes.section}>
          <Typography variant="h6">
            {t('search.page.storefrontContent.translations.title')}
          </Typography>
          <Tabs
            value={selectedLanguage}
            onChange={(_event, language) => setSelectedLanguage(language)}
            className={classes.tabs}
            variant="scrollable"
            scrollButtons="auto"
          >
            {enabledLanguages.map(language => (
              <Tab key={language} value={language} label={language.toUpperCase()} />
            ))}
          </Tabs>
          <Box className={classes.translationToolbar}>
            <TextField
              className={classes.search}
              size="small"
              variant="outlined"
              placeholder={t('search.page.storefrontContent.translations.search')}
              value={query}
              onChange={event => setQuery(event.target.value)}
              inputProps={{ 'aria-label': t('search.page.storefrontContent.translations.search') }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className={classes.searchIcon} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              size="small"
              startIcon={<ReplayIcon />}
              disabled={Object.keys(languageOverrides).length === 0}
              onClick={resetLanguage}
            >
              {t('search.page.storefrontContent.translations.resetAll')}
            </Button>
          </Box>
          <Box className={classes.translationList}>
            {translationKeys.map((key) => {
              const overridden = Object.prototype.hasOwnProperty.call(languageOverrides, key);
              const presentation = getTranslationPresentation(key);
              return (
                <Box key={key} className={classes.translationRow}>
                  <Box className={classes.translationMeta}>
                    <Box className={classes.translationTitleRow}>
                      <Typography className={classes.translationTitle}>
                        {presentation.label}
                      </Typography>
                      {overridden && (
                        <Chip
                          size="small"
                          className={classes.overrideChip}
                          label={t('search.page.storefrontContent.override')}
                        />
                      )}
                    </Box>
                    <Typography className={classes.translationDescription}>
                      {presentation.description}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    className={classes.translationInput}
                    value={languageOverrides[key] ?? catalog[key]}
                    onChange={event => updateTranslation(key, event.target.value)}
                    helperText={overridden
                      ? `${t('search.page.storefrontContent.default')}: ${catalog[key]}`
                      : undefined}
                  />
                  <Box className={classes.rowActions}>
                    {overridden && (
                      <Button size="small" onClick={() => updateTranslation(key, catalog[key])}>
                        {t('search.page.storefrontContent.translations.reset')}
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
          {translationKeys.length === 0 && (
            <Typography color="textSecondary" className={classes.emptyState}>
              {t('search.page.storefrontContent.translations.empty')}
            </Typography>
          )}
        </SettingsCard>

        {Object.keys(templates).length > 0 && (
          <SettingsCard className={classes.section}>
            <Typography variant="h6">
              {t('search.page.storefrontContent.templates.title')}
            </Typography>
            <Typography variant="body2" className={classes.intro}>
              {t('search.page.storefrontContent.templates.help')}
            </Typography>
            <Tabs
              value={selectedTemplate}
              onChange={(_event, templateId) => setSelectedTemplate(templateId)}
              className={classes.tabs}
              variant="scrollable"
              scrollButtons="auto"
            >
              {Object.values(templates).map(template => (
                <Tab key={template.id} value={template.id} label={template.name} />
              ))}
            </Tabs>
            {selectedTemplateDefinition && (
              <>
                <Box className={classes.translationToolbar}>
                  <Box>
                    <Typography variant="body2" className={classes.templateMeta}>
                      {selectedTemplateDefinition.id}
                    </Typography>
                    {Object.prototype.hasOwnProperty.call(templateOverrides, selectedTemplate) && (
                      <Chip size="small" className={classes.overrideChip} label={t('search.page.storefrontContent.override')} />
                    )}
                  </Box>
                  <Button
                    size="small"
                    startIcon={<ReplayIcon />}
                    disabled={!Object.prototype.hasOwnProperty.call(
                      templateOverrides,
                      selectedTemplate,
                    )}
                    onClick={() => updateTemplate(selectedTemplateDefinition.source)}
                  >
                    {t('search.page.storefrontContent.templates.reset')}
                  </Button>
                </Box>
                <textarea
                  className={classes.templateSource}
                  value={selectedTemplateSource}
                  onChange={event => updateTemplate(event.target.value)}
                  aria-label={t('search.page.storefrontContent.templates.source')}
                  spellCheck={false}
                />
              </>
            )}
          </SettingsCard>
        )}
        <Snackbar
          open={languageWarningOpen}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={() => setLanguageWarningOpen(false)}
        >
          <Alert severity="warning" onClose={() => setLanguageWarningOpen(false)}>
            {t('search.page.storefrontContent.languages.minimum')}
          </Alert>
        </Snackbar>
      </Box>
    </SearchFeaturePageShell>
  );
}
